import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { collection, query, where, getDocs, getCountFromServer, doc, updateDoc, orderBy, arrayUnion, arrayRemove, addDoc } from 'firebase/firestore';
import { httpsCallable } from '@firebase/functions';
import { db, functions } from './../firebase.js'; // Ensure these are correctly set up
import { useSecondHeader } from './SecondHeaderContext.js';
import { cache } from 'react';
import { getRecordsFromDisplayUnit } from './../hooks/unitDataHooks';
import { getAllCategories } from '../common/unitCategories';

// Utility Functions
const getStartOfYear = (date) => new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
const formatDate = (date) => {
    const d = new Date(date);
    const month = `${d.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${d.getUTCDate()}`.padStart(2, '0');
    return `${d.getUTCFullYear()}-${month}-${day}`;
};

// Create the context
const UnitDataCacheContext = createContext();

// Create a provider component
export const UnitDataCacheProvider = ({ children }) => {
    const [records, setRecords] = useState([]);

    const [categoryGroupedRecords, setCategoryGroupedRecords] = useState([]);
    const [heatmapRecords, setHeatmapRecords] = useState([]);
    const [barchartRecords, setBarchartRecords] = useState([]);

    const [activationsUnit, setActivationsUnit] = useState("år");
    const [activationsRelativePercentage, setActivationsRelativePercentage] = useState(NaN);
    const [activationsCounts, setActivationsCounts] = useState([]);
    const [activityHeatmapData, setActivityHeatmapData] = useState([]);
    const [activityOverTimeData, setActivityOverTimeData] = useState([]);
    const [activityOverTimeContext] = useState(undefined);
    const [cachedDateRanges, setCachedDateRanges] = useState([])
    const [cachedRecords, setCachedRecords] = useState([])
    const cachedRecordsRef = useRef([])

    const { currentUnit } = useSecondHeader();
    const [ lastUnitId, setLastUnitId ] = useState(undefined);

     // Compute the total activations
    const activationsTotal = () => {
        return activationsCounts.reduce((total, category) => total + category.count, 0);
    };

    const getOtherUserInfo = async (ids) => {
        try {
            const otherUsersInfoEndpoint = httpsCallable(functions, 'getUsersInfo');
            console.log("Firebase call")
            const result = await otherUsersInfoEndpoint({ user_ids: ids });
            return result.data;
        } catch (error) {
            console.error("Error fetching other user info: ", error);
            return [];
        }
    };

    const setCustomEval = async (record, category, unitId, userId, isAdmin) => {
        try {
            console.log("setCustomEval record", record);
            console.log("setCustomEval unitId", unitId);
            const recordRef = doc(db, 'units', unitId, "inference-records", record.record_id);
            if (isAdmin) {
                await updateDoc(recordRef, {
                    [`admin_evals.${userId}`]: category
                });

                return true;
            }

            await updateDoc(recordRef, {
                [`user_evals.${userId}`]: category
            });
            const recordCategoryCollection = collection(db, "record_categories")

            const _query = query(recordCategoryCollection, where("unit", "==", unitId), where("name", "==", category))
            getDocs(_query).then(snapshot => {
                const _doc = snapshot.docs[0]
                if (_doc) {
                    const snapshotDict = _doc.data() || {}
                    const votes = snapshotDict["votes"]

                    var newVotes = 1
                    if (votes) {
                        newVotes += votes
                    }

                    updateDoc(_doc.ref, {
                        "votes": newVotes
                    })
                } else {
                    const newDoc = {
                        "unit": unitId,
                        "name": category,
                        "votes": 1
                    }
                    // Add a new document with a generated id.
                    addDoc(recordCategoryCollection, newDoc).then(() => {
                        console.log("Document successfully written!");
                    }).catch((error) => {
                        console.error("Error writing recordCategoryCollection document: ", error);
                    });
                }
            })

            record.user_evals = { ...record.user_evals, [userId]: category };
            return true;
        } catch (error) {
            console.error("Error setting custom eval: ", error);
            return false;
        }
    };

    const getCustomCategoriesListName = (isAdmin) => {
        return isAdmin ? "aris_custom_categories" : "custom_categories"
    }

    const getUserEvalFieldName = (isAdmin) => {
        return isAdmin ? "admin_evals" : "user_evals"
    }

    const addCustomCategory = async (record, category, unitId, isAdmin) => {
        try {
            const recordRef = doc(db, 'units', unitId);
            const listName = getCustomCategoriesListName(isAdmin)
            await updateDoc(recordRef, listName, arrayUnion(category));
            return true;
        } catch (error) {
            console.error("cust- Error setting custom category: ", error);
            return false;
        }
    };

    const removeCustomEvalCategory = async (record, category, unitId, isAdmin) => {
        try {
            const recordRef = doc(db, 'units', unitId);
            const listName = getCustomCategoriesListName(isAdmin)
            await updateDoc(recordRef, listName, arrayRemove(category));
            return true;
        } catch (error) {
            console.error("cust- Error setting custom category: ", error);
            return false;
        }
    }

    const removeCustomEval = async (record, category, unitId, userId, isAdmin) => {
        try {
            const recordRef = doc(db, 'units', unitId, "inference-records", record.record_id);
            const listName = getUserEvalFieldName(isAdmin)
            await updateDoc(recordRef, `${listName}.${userId}`, null);
            
            const recordCategoryCollection = collection(db, "record_categories")
            console.log("Firebase - ")
            const _query = query(
                recordCategoryCollection,
                where("unit", "==", unitId),
                where("name", "==", category)
            )
            getDocs(_query).then(snapshot => {
                const _doc = snapshot.docs[0]
                if (_doc) {
                    const snapshotDict = _doc.data() || {}
                    const votes = snapshotDict["votes"]
                    
                    if (votes) {
                        updateDoc(_doc.ref, {
                            "votes": votes - 1
                        })
                    }
                }
            })
            return true;
        } catch (error) {
            console.error("Error removing custom eval: ", error);
            return false;
        }
    }

    const insertDateRangeSorted = (dateRange) => {
        var cachedDateRangesNewValue = []
        var dateRangePlaced = false
        var i = 0
        if(cachedDateRanges.length == 0) {
            cachedDateRangesNewValue.push(dateRange)
        } else {
            while (i < cachedDateRanges.length) {
                if (dateRangePlaced) {
                    cachedDateRangesNewValue.push(cachedDateRanges[i])
                } else {
                    if (cachedDateRanges[i][0] < dateRange[0] && cachedDateRanges[i][1] < dateRange[0]) {
                        cachedDateRangesNewValue.push(cachedDateRanges[i])
                    } else if (dateRange[0] < cachedDateRanges[i][0] && dateRange[1] < cachedDateRanges[i][0]) {
                        cachedDateRangesNewValue.push(dateRange)
                        cachedDateRangesNewValue.push(cachedDateRanges[i])
                        dateRangePlaced = true
                    } else {
                        const inspectedStartsWithinNew = cachedDateRanges[i][0] >= dateRange[0] && cachedDateRanges[i][0] <= dateRange[1]
                        const newStartsWithinInspected = dateRange[0] >= cachedDateRanges[i][0] && dateRange[0] <= cachedDateRanges[i][1]
            
                        if (newStartsWithinInspected || inspectedStartsWithinNew) {
                            var fusedRange = []
            
                            if (newStartsWithinInspected) {
                                fusedRange.push(cachedDateRanges[i][0])
                            } else {
                                fusedRange.push(dateRange[0])
                            }
            
                            var lastRangeEnd = undefined
                            var lastRangeFound = false
                            var lastRangeStart = 0
                            while (!lastRangeFound && i < cachedDateRanges.length) {
                                if (cachedDateRanges[i][0] <= dateRange[1]) {
                                    lastRangeStart = cachedDateRanges[i][0]
                                } else {
                                    lastRangeEnd = cachedDateRanges[i - 1][1]
                                    lastRangeFound = true
                                }
            
                                i += 1
                            }
                            i -= 1
                            lastRangeEnd = Math.max(dateRange[1], lastRangeEnd ?? cachedDateRanges[i][1])
                            
                            if (lastRangeFound) {
                                i -= 1
                            }
            
                            fusedRange.push(lastRangeEnd)
            
                            cachedDateRangesNewValue.push(fusedRange)
                            dateRangePlaced = true
                        }
                        
                    }
                }
        
                i += 1
            }
        }
        setCachedDateRanges(cachedDateRangesNewValue)
    }

    const addToCachedRecords = (records) => {
        if (records.length == 0) {
            return cachedRecordsRef.current
        }

        var newCachedRecords = []
        const cachedRecordsOldValue = [...cachedRecordsRef.current]
        if (cachedRecordsOldValue.length == 0) {
            newCachedRecords = records
        } else {
            
            let recordsHasBeenAdded = false
            let i = 0
            while (i < cachedRecordsOldValue.length) {
                if (recordsHasBeenAdded ) {
                    newCachedRecords.push(cachedRecordsOldValue[i])
                } else {
                    if ((cachedRecordsOldValue[i].timestamp > records[0].timestamp)) {
                        newCachedRecords.push(cachedRecordsOldValue[i])
                    } else {
                        const newRecords = records.filter(record => !cachedRecordsOldValue.some((cachedRecord) => {
                            const idMatch = cachedRecord.record_id === record.record_id
                            return idMatch
                        }))
                        // const newRecords = records
                        newCachedRecords = newCachedRecords.concat(newRecords)
                        i -= 1
                        recordsHasBeenAdded = true
                    }
                }
                i += 1
            }

            if (!recordsHasBeenAdded) {
                newCachedRecords = newCachedRecords.concat(records)
            }
        }
        // Update the ref immediately so concurrent calls see the latest value
        cachedRecordsRef.current = newCachedRecords
        setCachedRecords(newCachedRecords)

        return newCachedRecords
    }

    // Get records for a display_unit (takes display_unit object as argument)
    const getRecordsForDisplayUnit = async (display_unit, from, to, completion = setRecords) => {
        console.log("getRecordsForDisplayUnit display_unit", display_unit);
        if (!display_unit || !display_unit.unit_ranges || !Array.isArray(display_unit.unit_ranges) || display_unit.unit_ranges.length === 0) {
            console.warn("getRecordsForDisplayUnit: Invalid display_unit provided");
            completion([]);
            return;
        }

        try {
            // Get all records from display_unit's unit_ranges
            const allRecords = await getRecordsFromDisplayUnit(display_unit);
            console.log("getRecordsForDisplayUnit: Got", allRecords.length, "total records");
            
            // Filter records by the requested date range
            let outputRecords = allRecords.filter((record) => {
                const timeFilterSatisfied = record.timestamp >= from && record.timestamp <= to;
                return timeFilterSatisfied && (record.is_test !== true);
            });
            console.log("getRecordsForDisplayUnit: After time filter:", outputRecords.length, "records");

            // Apply category filtering
            var categories = getAllCategories(display_unit);
            console.log("getRecordsForDisplayUnit: Available categories:", categories);
            console.log("getRecordsForDisplayUnit: Sample record evals:", outputRecords.slice(0, 5).map(r => r.eval));
            
            const beforeFilter = outputRecords.length;
            outputRecords = outputRecords.filter(record => {
                const included = categories.includes(record.eval);
                if (!included) {
                    console.warn("Filtering out record with eval:", record.eval, "- not in categories:", categories);
                }
                return included;
            });
            console.log("getRecordsForDisplayUnit: After category filter:", beforeFilter, "→", outputRecords.length, "records");

            // Convert timestamps to Date objects
            outputRecords = outputRecords.map(record => ({
                ...record,
                timestamp: new Date(record.timestamp) // Convert to Date object
            }));

            completion(outputRecords);
        } catch (error) {
            console.error("records- Error fetching records from display_unit: ", error);
            completion([]);
        }
    };

    // Get records for a legacy unit (takes unitId as argument, uses currentUnit from context)
    const getRecords = async (unitId, from, to, completion = setRecords) => {
        console.log("getRecords unitId", unitId);
        console.log("getRecords from", from);
        console.log("getRecords to", to);
        if (!currentUnit) {
            return
        }

        // Legacy behavior: fetch from single unit
        var downloadNeededFrom = from
        var downloadNeededTo = to

        var i = 0
        var matchingRangeStartFound = false
        var missingRangeDetermined = false
        while (!missingRangeDetermined && i < cachedDateRanges.length) {
            if (!matchingRangeStartFound && (from >= cachedDateRanges[i][0] && from < cachedDateRanges[i][1])) {
                downloadNeededFrom = cachedDateRanges[i][1]
                matchingRangeStartFound = true
            }

            if (to <= cachedDateRanges[i][1] && to > cachedDateRanges[i][0]) {
                downloadNeededTo = cachedDateRanges[i][0]
                missingRangeDetermined = true
            } else if (matchingRangeStartFound && (to <= cachedDateRanges[i][0])) {
                missingRangeDetermined = true
            }

            i += 1
        }
        let newRecordsUnfiltered = []
        let newRecords = []
        let newCachedRecords = []
        insertDateRangeSorted([from, to])
        // Validate that we have valid dates and unitId before querying
        const downloadNeeded = downloadNeededFrom < downloadNeededTo && 
            downloadNeededFrom !== undefined && 
            downloadNeededTo !== undefined && 
            downloadNeededFrom !== null && 
            downloadNeededTo !== null &&
            unitId !== undefined &&
            unitId !== null
        if (downloadNeeded) {
            try {
                console.log("getRecords query", downloadNeededFrom, downloadNeededTo);
                console.log("origin from", from);
                console.log("origin to", to);
                const q = query(
                    collection(db, `/units/${unitId}/inference-records`),
                    where("timestamp", ">", downloadNeededFrom),
                    where("timestamp", "<", downloadNeededTo),
                    where("is_test", "!=", true),  // Filter out test records
                    orderBy("timestamp", "desc")
                );
                const snapshot = await getDocs(q);
                newRecords = snapshot.docs.map(doc => ({
                    ...doc.data(),
                    record_id: doc.id
                }));
                var categories = getAllCategories(currentUnit);
                console.log("getRecords: Categories for unit", currentUnit.id, ":", categories);
                console.log("getRecords: Sample records:", newRecords.slice(0, 5).map(r => ({ eval: r.eval, id: r.record_id })));
                
                const beforeFilter = newRecords.length;
                newRecords = newRecords.filter(record => {
                    const included = categories.includes(record.eval);
                    if (!included && beforeFilter < 100) { // Only log first few to avoid spam
                        console.warn("Filtering out record with eval:", record.eval);
                    }
                    return included;
                });
                console.log("getRecords: After category filter:", beforeFilter, "→", newRecords.length, "records");
                newCachedRecords = addToCachedRecords(newRecords)

            } catch (error) {
                console.error("records- Error fetching records: ", error);
            }

        } else {
            newCachedRecords = cachedRecordsRef.current
        }

        console.log("newCachedRecords, from, to, newRecords, downloadNeeded, newRecordsUnfiltered, downloadNeededFrom, downloadNeededTo, cachedRecords", newCachedRecords.length, from, to, newRecords.length, downloadNeeded, newRecordsUnfiltered.length, downloadNeededFrom, downloadNeededTo, cachedRecordsRef.current.length);
        let outputRecords = newCachedRecords.filter((record) => {
            const timeFilterSatisfied = record.timestamp >= from && record.timestamp <= to

            return timeFilterSatisfied && (record.is_test !== true)
        })

        outputRecords = outputRecords.map(record => ({
            ...record,
            timestamp: new Date(record.timestamp) // Convert to Date object
        }));

        completion(outputRecords)
    };

    const loadCategoryGroupedRecords = (unitId, from, to) => {
        // Check if currentUnit is a display_unit
        if (currentUnit && currentUnit.unit_ranges && Array.isArray(currentUnit.unit_ranges) && currentUnit.unit_ranges.length > 0) {
            getRecordsForDisplayUnit(currentUnit, from, to, setCategoryGroupedRecords);
        } else {
            getRecords(unitId, from, to, setCategoryGroupedRecords);
        }
    }

    const loadHeatmapRecords = (unitId, from, to) => {
        // Check if currentUnit is a display_unit
        if (currentUnit && currentUnit.unit_ranges && Array.isArray(currentUnit.unit_ranges) && currentUnit.unit_ranges.length > 0) {
            getRecordsForDisplayUnit(currentUnit, from, to, setHeatmapRecords);
        } else {
            getRecords(unitId, from, to, setHeatmapRecords);
        }
    }

    const loadBarchartRecords = (unitId, from, to) => {
        // Check if currentUnit is a display_unit
        if (currentUnit && currentUnit.unit_ranges && Array.isArray(currentUnit.unit_ranges) && currentUnit.unit_ranges.length > 0) {
            getRecordsForDisplayUnit(currentUnit, from, to, setBarchartRecords);
        } else {
            getRecords(unitId, from, to, setBarchartRecords);
        }
    }

    const getActivityOverTimeData = async (unitId, from, to, timeUnit) => {
    };

    const getActivityHeatmapData = async (unitId, from, to) => {
        console.log("getActivityHeatmapData currentUnit", currentUnit);
        console.log("from", from);
        console.log("to", to);
        if (currentUnit) {
            // Check if currentUnit is a display_unit
            const isDisplayUnit = currentUnit.unit_ranges && Array.isArray(currentUnit.unit_ranges) && currentUnit.unit_ranges.length > 0;
            const getRecordsMethod = isDisplayUnit ? getRecordsForDisplayUnit : getRecords;
            const recordsArg = isDisplayUnit ? currentUnit : unitId;

            getRecordsMethod(recordsArg, from, to, (records) => {
                console.log("getActivityHeatmapData records", records.length);
                console.log("from", from);
                console.log("to", to);

                var recordsByCategory = {}
                const categories = getAllCategories(currentUnit);
                for (const category of categories) {
                    recordsByCategory[category] = []
                }

                for (const record of records) {
                    const category = record.eval
                    if (recordsByCategory[category]) {
                        recordsByCategory[category].push(record)
                    }
                }

                const tempActivity = []
                for (const category of categories) {
                    const countsByDay = Array(7).fill(0);
                    for (const record of recordsByCategory[category]) {
                        const day = new Date(record.timestamp).getDay(); // Get day of the week
                        countsByDay[day]++;
                    }
                    countsByDay.unshift(...countsByDay.splice(1)); // Adjust to start from Monday
                    tempActivity.push({ category, counts: countsByDay });
                }
                for (let i = 0; i < tempActivity.length; i++) {
                    if (tempActivity[i].category === "UNDEFINED") {
                        tempActivity.push(tempActivity.splice(i, 1)[0]);
                        break;
                    }
                }
                setActivityHeatmapData(tempActivity);
            })
        } else {
            setActivityHeatmapData([])
        }
    };

    // TODO: Provide timeUnit instead of prevFrom and prevTo
    const getActivationsData = async (unitId, from, to, prevFrom, prevTo) => {
        if (!currentUnit) {
            return;
        }
        // Check if currentUnit is a display_unit
        const isDisplayUnit = currentUnit.unit_ranges && Array.isArray(currentUnit.unit_ranges) && currentUnit.unit_ranges.length > 0;
        const getRecordsMethod = isDisplayUnit ? getRecordsForDisplayUnit : getRecords;
        const recordsArg = isDisplayUnit ? currentUnit : unitId;

        var categories = getAllCategories(currentUnit);
        getRecordsMethod(recordsArg, from, to, (result) => {
            const tempActivationsCounts = categories.map(category => ({ category, count: 0 }));

            // Sets the UNDEFINED category last in the array
            for (let i = 0; i < tempActivationsCounts.length; i++) {
                if (tempActivationsCounts[i].category === "UNDEFINED") {
                    tempActivationsCounts.push(tempActivationsCounts.splice(i, 1)[0]);
                    break;
                }
            }

            result.forEach(record => {
                const categoryIndex = tempActivationsCounts.findIndex(tempActivationsCount => tempActivationsCount.category === record.eval);
                if (categoryIndex !== -1) {
                    tempActivationsCounts[categoryIndex].count += 1
                }
            })

            setActivationsCounts(tempActivationsCounts)

            getRecordsMethod(recordsArg, prevFrom, prevTo, (prevDataResult) => {
                const prevCount = prevDataResult.length
                const totalCount = result.length
                setActivationsRelativePercentage(
                    ((totalCount / prevCount - 1) * 100).toFixed(1)
                );
            })
        })
    };

    useEffect(() => {
        // Check if unit changed - for display_units, use a unique identifier
        const currentUnitId = currentUnit?.unit_id || (currentUnit?.unit_ranges?.[0]?.unit_id) || null;
        const currentUnitIdentifier = currentUnit?.unit_ranges ? 
            JSON.stringify(currentUnit.unit_ranges.map(r => `${r.unit_id}-${r.start_date}-${r.end_date}`)) : 
            currentUnitId;

        if (!currentUnit || lastUnitId !== currentUnitIdentifier) {
            if (cachedDateRanges && cachedDateRanges.length > 0) {
                setCachedDateRanges([])
            }
            if (cachedRecordsRef.current && cachedRecordsRef.current.length > 0) {
                cachedRecordsRef.current = []
                setCachedRecords([])
            }

            if (currentUnit) {
                setLastUnitId(currentUnitIdentifier)
            }
        }
    }, [currentUnit])

    return (
        <UnitDataCacheContext.Provider value={{
            records,
            activationsUnit,
            setActivationsUnit,
            activationsRelativePercentage,
            activationsCounts,
            activationsTotal,
            activityHeatmapData,
            activityOverTimeData,
            activityOverTimeContext,
            getOtherUserInfo,
            setCustomEval,
            removeCustomEval,
            getRecords,
            getRecordsForDisplayUnit,
            getActivityOverTimeData,
            getActivityHeatmapData,
            getActivationsData,
            addCustomCategory,
            removeCustomEvalCategory,

            categoryGroupedRecords,
            heatmapRecords,
            barchartRecords,

            loadCategoryGroupedRecords,
            loadHeatmapRecords,
            loadBarchartRecords,

            setRecords,
        }}>
            {children}
        </UnitDataCacheContext.Provider>
    );
};

// Custom hook to use the UnitDataCacheContext
export const useUnitDataCache = () => {
    return useContext(UnitDataCacheContext);
};
